'use client';

import React from 'react';
import { Plus, Search, Trash2 } from 'lucide-react';
import { Autocomplete, Box, Button, Checkbox, InputAdornment, MenuItem, TextField, Typography } from '@mui/material';
import type { TextFieldProps } from '@mui/material';
import { formatCurrency } from '@crm-src/lib/utils';
import {
  formatPriceListPrice,
  priceListSearchText,
  type PriceListCategory,
  type PriceListPricingMode,
  type PriceListUnit,
} from '@xop/dashboard-shared';

/**
 * One of the selected product's features, as the cascade serves it.
 *
 * It carries its PRICE since 2026-09-13: the price book merged into the
 * catalogue, so picking a feature fills the part number, the description AND
 * the unit price. `part_number` is the printed one (700-2341-001), falling
 * back server-side to the composed `<productCode>-<featureCode>`.
 */
export interface CatalogFeature {
  id: number;
  name?: string;
  description?: string;
  part_number?: string;
  unit: PriceListUnit;
  pricing_mode: PriceListPricingMode;
  price_cents: number;
  percent_rate: number | null;
  currency: string;
  category?: PriceListCategory;
  notes?: string;
  active?: boolean;
  priced?: boolean;
}

/** How one document line decides its part number, description and price. */
export type ProductSelectionMode = 'manual' | 'catalog';

export interface InvoiceProduct {
  id: string;
  partNumber: string;
  description: string;
  seats: number;
  chargePerSeat: number;
  amount: number;
  // Cascading selection state
  // - manual  — the operator types the part number, description and price
  // - catalog — Product → Feature, fills part number, description AND the
  //             unit price (CRM ▸ Pricing is where that price is set)
  //
  // There was a third mode until 2026-09-13 that picked off a separate price
  // list. The price list IS the catalogue now, so the same rows were being
  // offered twice.
  selectionMode: ProductSelectionMode;
  selectedProductId?: number;
  selectedFeatureId?: number;
  /** The book's unit price in CENTS at the moment the line was filled.
   * `chargePerSeat` is the same number in dollars, which is what the form's
   * arithmetic needs; this is the integer the price list actually stores, so
   * a reader can tell a CEO-set price from a hand-typed one without going
   * back through floating point. The reseller portal writes the same key
   * (spec §5), which is the point — one snapshot shape for both surfaces. */
  listPriceCents?: number;
  /** HOW the picked feature is priced, copied with the price (2026-09-13).
   * Absent on a manual line. `percent` is the one that matters downstream: the
   * form re-prices such a line off the other lines' subtotal on every change
   * (`resolvePercentLines`), and crm-api does the same on save, so the number
   * in `chargePerSeat` is derived and read-only rather than typed. */
  pricingMode?: PriceListPricingMode;
  /** Percentage points for a `percent` line (15 = "15% of order"). */
  percentRate?: number;
  // Discount exclusion flag - when true, this item won't have discounts applied
  excludeFromDiscount?: boolean;
}

export interface ProductHierarchy {
  products: any[];
  productFeatures: { [productId: number]: CatalogFeature[] };
}

export interface ProductValidationErrors {
  [productId: string]: {
    partNumber?: string;
    description?: string;
    seats?: string;
    chargePerSeat?: string;
  };
}

interface ProductsTableProps {
  products: InvoiceProduct[];
  productHierarchy: ProductHierarchy;
  priceInputs: { [productId: string]: string };
  setPriceInputs: React.Dispatch<React.SetStateAction<{ [productId: string]: string }>>;
  validationErrors: ProductValidationErrors | undefined;
  onAddProduct: () => void;
  onRemoveProduct: (productId: string) => void;
  onUpdateProduct: (productId: string, field: keyof InvoiceProduct, value: any) => void;
  onProductModeChange: (productRowId: string, mode: ProductSelectionMode) => void;
  onCascadingSelection: (productRowId: string, level: 'product' | 'feature', selectedId: number) => void;
}

export const ProductsTable: React.FC<ProductsTableProps> = ({
  products,
  productHierarchy,
  priceInputs,
  setPriceInputs,
  validationErrors,
  onAddProduct,
  onRemoveProduct,
  onUpdateProduct,
  onProductModeChange,
  onCascadingSelection,
}) => {
  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography component="h3" sx={{ fontSize: "1.125rem", fontWeight: 600 }}>Products &amp; Services</Typography>
        <Button
          type="button"
          variant="outlined"
          color="inherit"
          size="small"
          onClick={onAddProduct}
          startIcon={<Plus size={16} />}
        >
          Add Product
        </Button>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {/* Table Headers */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(13, 1fr)", gap: 1, alignItems: "center", py: 1, borderBottom: 1, borderColor: "divider", bgcolor: "surfaceSoft", borderTopLeftRadius: 1, borderTopRightRadius: 1, px: 1 }}>
          <Box sx={{ width: 20, flexShrink: 0 }}>
            <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>#</Typography>
          </Box>
          <Box sx={{ gridColumn: "span 3" }}>
            <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>Product Selection</Typography>
          </Box>
          <Box sx={{ gridColumn: "span 2" }}>
            <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>Part Number</Typography>
          </Box>
          <Box sx={{ gridColumn: "span 2" }}>
            <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>Description</Typography>
          </Box>
          <Box sx={{ gridColumn: "span 1" }}>
            <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>Quantity</Typography>
          </Box>
          <Box sx={{ gridColumn: "span 1" }}>
            <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>Unit Price</Typography>
          </Box>
          <Box sx={{ gridColumn: "span 1" }}>
            <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>Amount</Typography>
          </Box>
          <Box sx={{ gridColumn: "span 1", textAlign: "center" }}>
            <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }} title="Exclude this item from discount calculations">
              No Discount
            </Typography>
          </Box>
          <Box sx={{ gridColumn: "span 1" }}>
            <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>Action</Typography>
          </Box>
        </Box>

        {/* Product Rows */}
        {products.map((product, index) => {
          // A catalog line carries a selection once a product or one of its
          // features is picked. Drives the auto-fill highlight + whether the
          // part/description inputs are editable.
          const hasCatalogSelection = !!(
            product.selectedProductId || product.selectedFeatureId
          );
          // Step 2 ("Select Feature") after a Product is chosen: ONE flat,
          // searchable list of the product's OWN features, each showing what it
          // costs. Retired features stay out — a document that already names one
          // keeps it, but a NEW line must not be able to pick it. UNPRICED ones
          // are offered (2026-09-13), labelled "No price set": picking one
          // fills the part number and the description and leaves the unit
          // price for the operator to type — validation refuses a fixed line
          // still at 0, so "$0.00" never reaches a document by accident.
          const catalogOptions: CatalogFeature[] = (
            product.selectedProductId
              ? (productHierarchy.productFeatures[product.selectedProductId] || [])
              : []
          ).filter((f) => f.active !== false);
          // A "15% of order" line: its unit price is derived from the other
          // lines, so the cell is read-only and says why.
          const isPercentLine = product.pricingMode === 'percent';
          // A catalogue pick with no list price behind it: the operator has to
          // type one, and the line says so rather than sitting at a blank.
          const isUnpricedCatalogLine =
            product.selectionMode === 'catalog' &&
            product.selectedFeatureId != null &&
            product.pricingMode === 'fixed' &&
            product.listPriceCents === 0;
          const selectedCatalogOption = product.selectedFeatureId != null
            ? (catalogOptions.find((o) => o.id === product.selectedFeatureId) ?? null)
            : null;
          const showFeatureStep = !!product.selectedProductId && catalogOptions.length > 0;
          return (
          <Box key={product.id} sx={{ display: "grid", gridTemplateColumns: "repeat(13, 1fr)", gap: 1, alignItems: "start", py: 1, px: 1, border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "background.paper" }}>
            <Box sx={{ width: 20, flexShrink: 0, display: "flex", alignItems: "center", height: 40 }}>
              <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>{index + 1}</Typography>
            </Box>

            {/* Product Selection - Cascading Dropdowns */}
            <Box sx={{ gridColumn: "span 3" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {/* Selection Mode Toggle */}
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={product.selectionMode}
                  onChange={(e) => onProductModeChange(product.id, e.target.value as ProductSelectionMode)}
                >
                  <MenuItem value="manual">✏️ Manual Entry</MenuItem>
                  <MenuItem value="catalog">📦 Product Catalog</MenuItem>
                </TextField>

                {/* Cascading Dropdowns (only show when catalog mode) */}
                {product.selectionMode === 'catalog' && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    {/* Product Dropdown (step 1) — products only. Value Added
                        Services + Platform Extensions live in the Feature step
                        below, AFTER a product is chosen. */}
                    <TextField
                      select
                      size="small"
                      fullWidth
                      SelectProps={{ displayEmpty: true }}
                      value={product.selectedProductId?.toString() || ''}
                      onChange={(e) => onCascadingSelection(product.id, 'product', parseInt(e.target.value))}
                      sx={{ "& .MuiInputBase-input": { fontSize: 12 } }}
                    >
                      <MenuItem value="" disabled>1. Select Product</MenuItem>
                      {productHierarchy.products.map((prod) => (
                        <MenuItem key={prod.id} value={prod.id.toString()}>
                          <Box sx={{ display: "flex", flexDirection: "column", textAlign: "left" }}>
                            <Box component="span" sx={{ fontWeight: 500, fontSize: 12 }}>{prod.name}</Box>
                            <Box component="span" sx={{ fontSize: 12, color: "primary.main" }}>{prod.part_number}</Box>
                          </Box>
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Feature step (step 2) — after a product is chosen: ONE
                        flat, SEARCHABLE, scrollable list of the product's own
                        features (Value Added Services, Platform Extensions,
                        support items), each showing WHAT IT COSTS. Picking one
                        fills the part number, the description and the price. */}
                    {showFeatureStep && (
                      <Autocomplete
                        size="small"
                        fullWidth
                        options={catalogOptions}
                        value={selectedCatalogOption}
                        getOptionLabel={(o) => o.description || o.name || ''}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        filterOptions={(opts, state) => {
                          const q = state.inputValue.trim().toLowerCase();
                          if (!q) return opts;
                          return opts.filter((o) =>
                            priceListSearchText({
                              part_number: o.part_number ?? '',
                              description: o.description || o.name || '',
                              notes: o.notes ?? '',
                            }).includes(q),
                          );
                        }}
                        onChange={(_e, option) => {
                          if (!option) return;
                          onCascadingSelection(product.id, 'feature', option.id);
                        }}
                        ListboxProps={{ style: { maxHeight: 300 } }}
                        renderOption={(props, o) => {
                          const { key: _k, ...liProps } = props as any;
                          return (
                            <Box component="li" key={`feat-${o.id}`} {...liProps}>
                              <Box sx={{ display: "flex", flexDirection: "column", textAlign: "left" }}>
                                <Box component="span" sx={{ fontWeight: 500, fontSize: 12 }}>{o.description || o.name}</Box>
                                <Box component="span" sx={{ fontSize: 12, color: "text.secondary" }}>
                                  {o.part_number ? `${o.part_number} · ` : ''}{formatPriceListPrice(o)}
                                </Box>
                              </Box>
                            </Box>
                          );
                        }}
                        renderInput={(params) => {
                          // AutocompleteRenderInputParams doesn't line up with
                          // TextFieldProps under exactOptionalPropertyTypes — narrow
                          // it (valid at runtime), same as create-recurring-schedule-modal.
                          const inputProps = params as TextFieldProps;
                          return (
                            <TextField
                              {...inputProps}
                              size="small"
                              placeholder="2. Select Feature — type to search…"
                              sx={{ "& .MuiInputBase-input": { fontSize: 12 } }}
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Box sx={{ color: "text.disabled", display: "flex" }}><Search size={14} /></Box>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          );
                        }}
                      />
                    )}

                    {/* Status indicator */}
                    {product.selectionMode === 'catalog' && hasCatalogSelection && (
                      <Box sx={{ fontSize: 12, color: "success.main" }}>
                        ✅ Auto-filled from catalog
                      </Box>
                    )}
                    {isUnpricedCatalogLine && (
                      <Box sx={{ fontSize: 12, color: "warning.main" }}>
                        No list price — enter the unit price
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{ gridColumn: "span 2" }}>
              <TextField
                size="small"
                fullWidth
                value={product.partNumber}
                onChange={(e) => {
                  // Switch to manual mode if editing auto-filled fields
                  if (product.selectionMode === 'catalog') {
                    onProductModeChange(product.id, 'manual');
                  }
                  onUpdateProduct(product.id, 'partNumber', e.target.value);
                }}
                placeholder="Part No."
                disabled={product.selectionMode === 'catalog' && !hasCatalogSelection}
                sx={{
                  ...(product.selectionMode === 'catalog' && hasCatalogSelection ? { "& .MuiOutlinedInput-root": { bgcolor: (t) => t.palette.action.hover } } : {}),
                  ...(validationErrors?.[product.id]?.partNumber ? { "& .MuiOutlinedInput-notchedOutline": { borderColor: "error.main" } } : {}),
                }}
              />
              {validationErrors?.[product.id]?.partNumber && (
                <Typography component="p" sx={{ fontSize: 12, color: "error.main", mt: 0.5 }}>{validationErrors[product.id].partNumber}</Typography>
              )}
            </Box>
            <Box sx={{ gridColumn: "span 2" }}>
              <TextField
                size="small"
                fullWidth
                value={product.description}
                onChange={(e) => {
                  // Switch to manual mode if editing auto-filled fields
                  if (product.selectionMode === 'catalog') {
                    onProductModeChange(product.id, 'manual');
                  }
                  onUpdateProduct(product.id, 'description', e.target.value);
                }}
                placeholder="Description *"
                disabled={product.selectionMode === 'catalog' && !hasCatalogSelection}
                sx={{
                  ...(product.selectionMode === 'catalog' && hasCatalogSelection ? { "& .MuiOutlinedInput-root": { bgcolor: (t) => t.palette.action.hover } } : {}),
                  ...(validationErrors?.[product.id]?.description ? { "& .MuiOutlinedInput-notchedOutline": { borderColor: "error.main" } } : {}),
                }}
              />
              {validationErrors?.[product.id]?.description && (
                <Typography component="p" sx={{ fontSize: 12, color: "error.main", mt: 0.5 }}>{validationErrors[product.id].description}</Typography>
              )}
            </Box>
            <Box sx={{ gridColumn: "span 1" }}>
              <TextField
                type="text"
                size="small"
                fullWidth
                value={product.seats === 0 ? '' : product.seats.toString()}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow any input while typing, including empty
                  if (value === '') {
                    onUpdateProduct(product.id, 'seats', 0);
                    return;
                  }
                  // Only allow digits
                  if (/^\d+$/.test(value)) {
                    const numValue = parseInt(value);
                    onUpdateProduct(product.id, 'seats', numValue);
                  }
                }}
                onBlur={(e) => {
                  // On blur, ensure we have at least 1
                  const value = e.target.value;
                  if (value === '' || parseInt(value) <= 0) {
                    onUpdateProduct(product.id, 'seats', 1);
                  }
                }}
                placeholder="Qty *"
                {...(validationErrors?.[product.id]?.seats ? { sx: { "& .MuiOutlinedInput-notchedOutline": { borderColor: "error.main" } } } : {})}
              />
              {validationErrors?.[product.id]?.seats && (
                <Typography component="p" sx={{ fontSize: 12, color: "error.main", mt: 0.5 }}>{validationErrors[product.id].seats}</Typography>
              )}
            </Box>
            <Box sx={{ gridColumn: "span 1" }}>
              {isPercentLine ? (
                <>
                  {/* Derived, not typed: rate × the other lines' subtotal. The
                      typed-text echo (`priceInputs`) is deliberately not
                      consulted — the number here is whatever the resolver said
                      on this render. */}
                  <TextField
                    type="text"
                    size="small"
                    fullWidth
                    value={product.chargePerSeat.toFixed(2)}
                    InputProps={{ readOnly: true }}
                    title={`${product.percentRate ?? 0}% of the order's priced lines`}
                    sx={{
                      "& .MuiOutlinedInput-root": { bgcolor: "surfaceSoft" },
                      ...(validationErrors?.[product.id]?.chargePerSeat ? { "& .MuiOutlinedInput-notchedOutline": { borderColor: "error.main" } } : {}),
                    }}
                  />
                  <Typography component="p" sx={{ fontSize: 12, color: "text.secondary", mt: 0.5, whiteSpace: "nowrap" }}>
                    {product.percentRate ?? 0}% of order
                  </Typography>
                  {validationErrors?.[product.id]?.chargePerSeat && (
                    <Typography component="p" sx={{ fontSize: 12, color: "error.main", mt: 0.5 }}>{validationErrors?.[product.id]?.chargePerSeat}</Typography>
                  )}
                </>
              ) : (
              <>
              <TextField
                type="text"
                size="small"
                fullWidth
                value={priceInputs[product.id] !== undefined
                  ? priceInputs[product.id]
                  : (product.chargePerSeat === 0 ? '' : product.chargePerSeat.toFixed(2))
                }
                onChange={(e) => {
                  const value = e.target.value;
                  // Store the string value to preserve trailing zeros
                  setPriceInputs(prev => ({ ...prev, [product.id]: value }));

                  // Allow any input while typing, including empty
                  if (value === '') {
                    onUpdateProduct(product.id, 'chargePerSeat', 0);
                    return;
                  }
                  // Allow digits and decimal point (including trailing decimal/zeros)
                  if (/^\d*\.?\d*$/.test(value)) {
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue)) {
                      onUpdateProduct(product.id, 'chargePerSeat', numValue);
                    } else if (value.endsWith('.')) {
                      // Allow typing decimal point
                      onUpdateProduct(product.id, 'chargePerSeat', 0);
                    }
                  }
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  if (value === '' || isNaN(parseFloat(value))) {
                    onUpdateProduct(product.id, 'chargePerSeat', 0);
                    setPriceInputs(prev => ({ ...prev, [product.id]: '' }));
                  } else {
                    // Format to 2 decimal places on blur
                    const numValue = parseFloat(value);
                    const formatted = numValue.toFixed(2);
                    onUpdateProduct(product.id, 'chargePerSeat', parseFloat(formatted));
                    setPriceInputs(prev => ({ ...prev, [product.id]: formatted }));
                  }
                }}
                placeholder="Price *"
                {...(validationErrors?.[product.id]?.chargePerSeat ? { sx: { "& .MuiOutlinedInput-notchedOutline": { borderColor: "error.main" } } } : {})}
              />
              {validationErrors?.[product.id]?.chargePerSeat && (
                <Typography component="p" sx={{ fontSize: 12, color: "error.main", mt: 0.5 }}>{validationErrors[product.id].chargePerSeat}</Typography>
              )}
              </>
              )}
            </Box>
            <Box sx={{ gridColumn: "span 1" }}>
              <TextField
                type="text"
                size="small"
                fullWidth
                value={formatCurrency(product.amount, false)}
                InputProps={{ readOnly: true }}
                sx={{ "& .MuiOutlinedInput-root": { bgcolor: "surfaceSoft" } }}
              />
            </Box>

            {/* No Discount Checkbox */}
            <Box sx={{ gridColumn: "span 1", display: "flex", alignItems: "center", height: 40, justifyContent: "center" }}>
              <Checkbox
                checked={product.excludeFromDiscount || false}
                onChange={(_e, checked) => onUpdateProduct(product.id, 'excludeFromDiscount', checked)}
                title="Exclude this item from discount calculations"
              />
            </Box>

            <Box sx={{ gridColumn: "span 1", display: "flex", alignItems: "center", height: 40, justifyContent: "center" }}>
              {products.length > 1 && (
                <Button
                  type="button"
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={() => onRemoveProduct(product.id)}
                  sx={{ minWidth: 0, px: 1 }}
                >
                  <Trash2 size={16} />
                </Button>
              )}
            </Box>
          </Box>
          );
        })}
      </Box>
    </>
  );
};
